// client/src/components/TicketPrinter.jsx
import React, { useState, useEffect, useRef } from 'react';
import { renderTicketHtml } from '@mostajs/ticket-print';
import { qrSvg } from '@mostajs/qrpanel';
import { shapeMotifSvg } from '@mostajs/themes';
import { useConfig } from '../contexts/ConfigContext';
import { useSocket } from '../contexts/SocketContext';
import printerSettingsService from '../services/printerSettingsService';
import { getEffectiveThemes } from '../constants/builtinThemes';

/**
 * Composant gérant l'impression des tickets
 * Utilise l'API d'impression du navigateur pour imprimer sur l'imprimante locale
 * et synchronise les paramètres avec le serveur
 */
const TicketPrinter = ({ ticket, onPrintComplete, autoprint = false }) => {
  const { config } = useConfig();
  const { socket } = useSocket();
  // L'ID de station est généré à partir de l'adresse MAC ou d'un identifiant unique stocké
  const [stationId, setStationId] = useState(localStorage.getItem('ticketflow-station-id') || '');
  const [printerSettings, setPrinterSettings] = useState({
    printerName: localStorage.getItem('ticketflow-printer-name') || '',
    paperWidth: localStorage.getItem('ticketflow-paper-width') || '58',
    paperHeight: localStorage.getItem('ticketflow-paper-height') || 'auto',
    fontSize: localStorage.getItem('ticketflow-font-size') || '12',
    autoPrint: localStorage.getItem('ticketflow-auto-print') === 'true'
  });
  const [showSettings, setShowSettings] = useState(false);
  const [showStationId, setShowStationId] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printError, setPrintError] = useState(null);
  const printFrameRef = useRef(null);

  // Générer ou récupérer l'ID de station
  useEffect(() => {
    if (!stationId) {
      // Générer un ID de station unique basé sur la date et un random
      const newStationId = `station-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem('ticketflow-station-id', newStationId);
      setStationId(newStationId);
    } else {
      // Charger les paramètres d'impression depuis le serveur
      const loadServerSettings = async () => {
        try {
          const response = await printerSettingsService.getPrinterSettings(stationId);
          if (response.success && response.data) {
            const serverSettings = response.data;
            setPrinterSettings({
              printerName: serverSettings.printerName || '',
              paperWidth: serverSettings.paperWidth.toString() || '58',
              paperHeight: serverSettings.paperHeight || 'auto',
              fontSize: serverSettings.fontSize.toString() || '12',
              autoPrint: serverSettings.autoPrint
            });
            
            // Mettre à jour le localStorage pour cohérence
            localStorage.setItem('ticketflow-printer-name', serverSettings.printerName || '');
            localStorage.setItem('ticketflow-paper-width', serverSettings.paperWidth.toString() || '58');
            localStorage.setItem('ticketflow-paper-height', serverSettings.paperHeight || 'auto');
            localStorage.setItem('ticketflow-font-size', serverSettings.fontSize.toString() || '12');
            localStorage.setItem('ticketflow-auto-print', serverSettings.autoPrint.toString());
          }
        } catch (error) {
          console.error('Erreur lors du chargement des paramètres d\'impression:', error);
          // En cas d'erreur, on conserve les paramètres locaux
        }
      };
      
      loadServerSettings();
    }
  }, [stationId]);
  
  // Écouter les mises à jour des paramètres d'impression
  useEffect(() => {
    if (socket && stationId) {
      socket.on('printerSettingsUpdated', (data) => {
        if (data.stationId === stationId) {
          const updatedSettings = data.settings;
          setPrinterSettings({
            printerName: updatedSettings.printerName || '',
            paperWidth: updatedSettings.paperWidth.toString() || '58',
            paperHeight: updatedSettings.paperHeight || 'auto',
            fontSize: updatedSettings.fontSize.toString() || '12',
            autoPrint: updatedSettings.autoPrint
          });
          
          // Mettre à jour le localStorage
          localStorage.setItem('ticketflow-printer-name', updatedSettings.printerName || '');
          localStorage.setItem('ticketflow-paper-width', updatedSettings.paperWidth.toString() || '58');
          localStorage.setItem('ticketflow-paper-height', updatedSettings.paperHeight || 'auto');
          localStorage.setItem('ticketflow-font-size', updatedSettings.fontSize.toString() || '12');
          localStorage.setItem('ticketflow-auto-print', updatedSettings.autoPrint.toString());
        }
      });
      
      return () => {
        socket.off('printerSettingsUpdated');
      };
    }
  }, [socket, stationId]);

  // Appliquer l'impression automatique si configurée et demandée
  useEffect(() => {
    if (ticket && autoprint && printerSettings.autoPrint) {
      printTicket();
    }
  }, [ticket, autoprint, printerSettings.autoPrint]);

  // Sauvegarder les paramètres d'impression localement et sur le serveur
  const saveSettings = async () => {
    // Sauvegarder localement
    localStorage.setItem('ticketflow-printer-name', printerSettings.printerName);
    localStorage.setItem('ticketflow-paper-width', printerSettings.paperWidth);
    localStorage.setItem('ticketflow-paper-height', printerSettings.paperHeight);
    localStorage.setItem('ticketflow-font-size', printerSettings.fontSize);
    localStorage.setItem('ticketflow-auto-print', printerSettings.autoPrint);
    
    // Sauvegarder sur le serveur
    if (stationId) {
      try {
        await printerSettingsService.updatePrinterSettings(stationId, {
          printerName: printerSettings.printerName,
          paperWidth: parseInt(printerSettings.paperWidth, 10),
          paperHeight: printerSettings.paperHeight,
          fontSize: parseInt(printerSettings.fontSize, 10),
          autoPrint: printerSettings.autoPrint
        });
      } catch (error) {
        console.error('Erreur lors de la sauvegarde des paramètres sur le serveur:', error);
        // Continuer même en cas d'erreur - les paramètres locaux sont toujours sauvegardés
      }
    }
    
    setShowSettings(false);
  };

  // Générer le contenu HTML du ticket
  /**
   * HTML du ticket, produit par le MÊME moteur que le distributeur Electron
   * (`@mostajs/ticket-print`). Auparavant ce composant composait son propre gabarit :
   * il imprimait `ticket.id` — la clé TECHNIQUE — au lieu du libellé serveur, sans QR,
   * sans code-barres, sans motif, sans ordre de langues, et poussait le logo par URL
   * relative. Deux moteurs distincts pour un même ticket finissaient forcément par
   * diverger ; il n'y en a plus qu'un, alimenté ici avec exactement les mêmes entrées.
   */
  const generateTicketHTML = () => {
    const cfg = config || {};
    const agency = cfg.agency || {};
    const ui = cfg.ui || {};
    const print = cfg.ticketPrint || {};
    const suivi = cfg.ticketSuivi || {};

    // Forme de carte du thème actif — même résolution que DynamicStylesProvider.
    const carte =
      (getEffectiveThemes(cfg.themes).find((t) => t.id === (cfg.activeThemeId || 'default'))
        ?.serviceCard) || {};
    let motifSvg = null;
    try {
      motifSvg = shapeMotifSvg(carte.shape || 'rectangle', '#000');
    } catch (e) {
      /* forme sans motif (rectangle) → null, le module gère */
    }

    // Papier réglé sur le poste : 80 mm sinon 58 mm (thermique par défaut).
    const paper = parseInt(printerSettings.paperWidth, 10) === 80 ? '80mm' : '58mm';

    /**
     * URL portée par le QR, identique à l'Electron : /t/<site>/<service>/<ticket>.
     * Les trois identifiants sont nécessaires — le suivi reçoit TOUS les serveurs, prod
     * et test confondus, deux tickets de même numéro sur deux sites seraient sinon
     * indiscernables.
     */
    const urlSuivi =
      suivi.enabled && suivi.url && suivi.siteId && ticket.ticketId
        ? `${String(suivi.url).replace(/\/+$/, '')}/t/${encodeURIComponent(suivi.siteId)}` +
          `/${encodeURIComponent(ticket.serviceType || '')}/${encodeURIComponent(ticket.ticketId)}`
        : null;

    /**
     * QR pré-rendu ICI, dans le navigateur, et passé au module en `qrSvg`. La variante
     * asynchrone du module irait le chercher via un import dynamique de sous-voie, que le
     * bundle ne résout pas toujours ; l'encoder ici garantit sa présence sur le papier.
     * Repli sur le numéro du ticket quand le suivi n'est pas configuré, comme l'Electron.
     */
    let qrSvgRendu;
    if (print.showQRCode) {
      const charge = urlSuivi || String(ticket.ticketId || ticket.number || '');
      try {
        if (charge) qrSvgRendu = qrSvg(charge, { scale: 4, margin: 2 });
      } catch (e) {
        /* charge non encodable → ticket sans QR, jamais d'échec d'impression */
      }
    }

    /**
     * Logo par URL du serveur : le navigateur la charge (même origine). Pas de base64
     * ici — contrairement à l'Electron qui rend dans un document `data:` sans origine où
     * une URL relative ne se résout pas.
     */
    const logo =
      print.showLogo && agency.logo
        ? /^https?:\/\//.test(agency.logo)
          ? agency.logo
          : window.location.origin + agency.logo
        : undefined;

    const service = (cfg.serviceTypes || []).find((s) => s.id === ticket.serviceType) || {};

    return renderTicketHtml(
      {
        // Libellé composé par le SERVEUR (« إستقبال 20 ») : la valeur même que l'écran
        // affiche et que la voix annonce. Replis en cascade vers l'identifiant puis le
        // numéro nu pour un serveur ancien.
        number: ticket.label || ticket.ticketId || ticket.number,
        globalNumber: ticket.globalNumber,
        waitingAhead: ticket.waitingAhead,
        service: {
          fr: service.name || ticket.serviceName || ticket.serviceType,
          ar: service.nameAr || ticket.serviceNameAr,
          en: service.nameEn || ticket.serviceNameEn,
        },
        createdAt: ticket.timestamp || ticket.createdAt,
        serverName: ticket.serverName,
      },
      {
        agency: {
          name: agency.name,
          company: agency.company,
          companySlogan: agency.companySlogan,
          logo,
        },
        instruction: ui.ticketInstruction,
        paper,
        locale: 'fr',
        bilingual: ui.bilingual !== false,
        motifSvg,
        motifMode: carte.motifMode || 'coin',
        template: print.template,
        qrSvg: qrSvgRendu,
        barcode: print.showBarcode,
        numbering: ticket.numbering,
        languages: ticket.languages,
        ...(print.marginTop ? { margins: { top: print.marginTop } } : {}),
      },
    );
  };

  // Imprimer le ticket
  const printTicket = () => {
    if (!ticket || isPrinting) return;
    
    try {
      setIsPrinting(true);
      setPrintError(null);
      
      // Créer l'iframe pour l'impression si elle n'existe pas
      if (!printFrameRef.current) {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.top = '-10000px';
        iframe.style.left = '-10000px';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.visibility = 'hidden';
        iframe.style.opacity = '0';
        iframe.style.border = 'none';
        iframe.style.zIndex = '-9999';
        document.body.appendChild(iframe);
        printFrameRef.current = iframe;
      }
      
      // Définir le contenu de l'iframe
      const frameDoc = printFrameRef.current.contentDocument || printFrameRef.current.contentWindow.document;
      frameDoc.open();
      frameDoc.write(generateTicketHTML());
      frameDoc.close();
      
      // Attendre que tout soit chargé
      printFrameRef.current.onload = () => {
        setTimeout(() => {
          try {
            // Configurer l'impression pour impression directe sans dialogue
            const printWindow = printFrameRef.current.contentWindow;
            
            // Essayer d'utiliser l'API d'impression silencieuse si disponible
            if (printerSettings.printerName && window.chrome && window.chrome.runtime) {
              // Pour les applications Chrome/Extensions avec permissions
              try {
                const printOptions = {
                  printerName: printerSettings.printerName,
                  silent: true,
                  shouldPrintBackgrounds: false,
                  shouldPrintSelectionOnly: false
                };
                printWindow.print(printOptions);
              } catch (err) {
                // Fallback vers impression standard
                printWindow.print();
              }
            } else {
              // Impression standard - le navigateur gérera l'impression
              printWindow.print();
            }
            
            // Notification de fin d'impression
            setTimeout(() => {
              setIsPrinting(false);
              if (onPrintComplete) onPrintComplete();
            }, 1000);
          } catch (error) {
            console.error('Erreur lors de l\'impression:', error);
            setPrintError('Erreur lors de l\'impression: ' + error.message);
            setIsPrinting(false);
          }
        }, 500);
      };
    } catch (error) {
      console.error('Erreur lors de la préparation de l\'impression:', error);
      setPrintError('Erreur lors de la préparation de l\'impression: ' + error.message);
      setIsPrinting(false);
    }
  };

  // Si pas de ticket, ne rien afficher
  if (!ticket) return null;

  return (
    <div className="ticket-printer">
      {/* Bouton d'impression */}
      <div className="flex flex-col items-center mt-4">
        {printError && (
          <div className="bg-red-100 text-red-700 p-3 rounded mb-3 w-full text-center">
            {printError}
          </div>
        )}
        
        <div className="flex gap-2">
          <button
            onClick={printTicket}
            disabled={isPrinting}
            className={`px-4 py-2 rounded ${isPrinting 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-500 text-white hover:bg-blue-600'}`}
          >
            {isPrinting ? 'Impression en cours...' : 'Imprimer le ticket'}
          </button>
          
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            ⚙️ Paramètres
          </button>
        </div>
      </div>
      
      {/* Panneau de configuration de l'imprimante */}
      {showSettings && (
        <div className="mt-4 p-4 border rounded-lg bg-gray-50">
          <h3 className="text-lg font-semibold mb-3">Paramètres d'impression</h3>
          
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom de l'imprimante
            </label>
            <input
              type="text"
              value={printerSettings.printerName}
              onChange={(e) => setPrinterSettings(prev => ({ ...prev, printerName: e.target.value }))}
              placeholder="Laisser vide pour imprimante par défaut"
              className="w-full p-2 border rounded"
            />
            <p className="text-xs text-gray-500 mt-1">
              Note: Le nom exact de l'imprimante est nécessaire pour Chrome/Edge
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Largeur du papier (mm)
              </label>
              <select
                value={printerSettings.paperWidth}
                onChange={(e) => setPrinterSettings(prev => ({ ...prev, paperWidth: e.target.value }))}
                className="w-full p-2 border rounded"
              >
                <option value="58">58mm (thermique standard)</option>
                <option value="80">80mm (thermique large)</option>
                <option value="72">72mm (A7)</option>
                <option value="105">105mm (A6)</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Taille de police
              </label>
              <select
                value={printerSettings.fontSize}
                onChange={(e) => setPrinterSettings(prev => ({ ...prev, fontSize: e.target.value }))}
                className="w-full p-2 border rounded"
              >
                <option value="9">Petite</option>
                <option value="12">Moyenne</option>
                <option value="14">Grande</option>
                <option value="16">Très grande</option>
              </select>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={printerSettings.autoPrint}
                onChange={(e) => setPrinterSettings(prev => ({ ...prev, autoPrint: e.target.checked }))}
                className="mr-2"
              />
              <span>Impression automatique des nouveaux tickets</span>
            </label>
          </div>
          
          <div className="mt-2 text-sm text-gray-500">
            <p>
              Station ID: 
              {showStationId ? (
                <span className="font-mono ml-1">{stationId}</span>
              ) : (
                <button 
                  onClick={() => setShowStationId(true)}
                  className="ml-1 text-blue-500 underline"
                >
                  afficher
                </button>
              )}
            </p>
            <p>Ces paramètres sont synchronisés avec le serveur et peuvent être modifiés par l'administrateur.</p>
          </div>
          
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => setShowSettings(false)}
              className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Annuler
            </button>
            <button
              onClick={saveSettings}
              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Enregistrer
            </button>
          </div>
        </div>
      )}
      
      {/* iframe invisible pour l'impression */}
    </div>
  );
};

export default TicketPrinter;